import { switchMap, pluck, map, debounceTime, mergeMap } from 'rxjs/operators';
import { ofType, combineEpics } from 'redux-observable';

import { AppEpic } from 'common/epics/appEpic';
import { AppState } from 'common/appState';

import * as actions from '../actions/paginationActions';
import { PaginationService } from '../services/paginationService';
import {
    PaginationRequest,
    PaginationSuccess,
    PaginationView,
    AutocompleteRequest,
    InitialValueDataRequest,
    PaginationSuccessCallback,
} from '../models/paginationModel';
import { isUndefined } from 'lodash';
import { withUnarchivedDefault } from '../helpers/archivedDefault';
import { withArchivedParam } from '../helpers/archivedParam';

export const paginationEpicFactory = (paginationService: PaginationService): AppEpic => {
    const paginationEpic: AppEpic = (action$, store) =>
        action$.pipe(
            ofType(actions.PAGINATION_REQUESTED),
            pluck('payload'),
            mergeMap((request: PaginationRequest) => {
                const state: AppState = store.getState();

                const contractFilters = state.pagination.filters.find(({ paginatedView }) => {
                    // temporary solution to use one filter set to 2 paginated views
                    const isContractsFilter = paginatedView === PaginationView.Contracts;
                    const isMyContractsView = request.view === PaginationView.MyContracts;

                    return (
                        (isContractsFilter && isMyContractsView) || paginatedView === request.view
                    );
                });

                const benefitFilters = state.pagination.filters.find(({ paginatedView }) => {
                    // temporary solution to use one filter set to 2 paginated views
                    const isBenefitsFilter = paginatedView === PaginationView.Benefits;
                    const isMyBenefitsView = request.view === PaginationView.MyBenefits;

                    return (isBenefitsFilter && isMyBenefitsView) || paginatedView === request.view;
                });

                const finalFilter = contractFilters
                    ? contractFilters
                    : benefitFilters
                    ? benefitFilters
                    : undefined;

                /* Archived rows are hidden unless the view's stored filters say
                   otherwise. Applied here rather than in each view because
                   PaginatedTable fetches without filters and this epic is the one
                   place every paginated request re-reads them, so mount, paging,
                   drawer-apply and the refetch after archiving all go through it.
                   withArchivedParam then sends that choice as `archived` to the
                   endpoints that do not know `show_archived`. */
                const filters = withArchivedParam(
                    request.view,
                    withUnarchivedDefault(request.view, finalFilter && finalFilter.values),
                );

                if (
                    request.view === PaginationView.ContractBenefits &&
                    isUndefined(request.requestParams)
                ) {
                    if (!state.benefit.contractBenefits || !state.benefit.contractBenefits.length) {
                        return Promise.resolve(
                            actions.paginationSuccess({
                                ...request,
                                results: [],
                                count: 0,
                                next: '',
                                previous: '',
                            }),
                        );
                    }
                    const contractBenefitsRequest = {
                        ...request,
                        requestParams: { contractId: state.benefit.contractBenefits[0].contract },
                    };
                    return paginationService
                        .paginate({
                            ...contractBenefitsRequest,
                            filters,
                        })
                        .then((response) => actions.paginationSuccess({ ...response, ...request }))
                        .catch(actions.paginationFailed);
                }

                return paginationService
                    .paginate({ ...request, filters })
                    .then((response) => actions.paginationSuccess({ ...response, ...request }))
                    .catch(actions.paginationFailed);
            }),
        );

    const paginationSuccessEpic: AppEpic = (action$) =>
        action$.pipe(
            ofType(actions.PAGINATION_SUCCEED),
            pluck('payload'),
            map(({ paginationSuccessCallback, results }: PaginationSuccess) =>
                results
                    ? paginationSuccessCallback(results)
                    : actions.paginationFailed({ message: 'Pagination error' }),
            ),
        );

    const clearPaginationEpic: AppEpic = (action$) =>
        action$.pipe(
            ofType(actions.CLEAR_PAGINATION),
            pluck('payload'),
            map(({ paginationSuccessCallback }: PaginationSuccessCallback) =>
                paginationSuccessCallback([]),
            ),
        );

    const autocompleteEpic: AppEpic = (action$) =>
        action$.pipe(
            ofType(actions.AUTOCOMPLETE_REQUESTED),
            debounceTime(300),
            pluck('payload'),
            switchMap(({ searchTerm, searchParams, ...request }: AutocompleteRequest) =>
                paginationService
                    .autocomplete({ searchTerm, searchParams, ...request })
                    .then((response) => actions.autocompleteSuccess({ ...response, ...request }))
                    .catch(({ message }) =>
                        actions.autocompleteFailed({
                            ...request,
                            message,
                        }),
                    ),
            ),
        );

    const getAutocompleteInitialValueEpic: AppEpic = (action$) =>
        action$.pipe(
            ofType(actions.GET_INITIAL_VALUE_DATA_REQUESTED),
            pluck('payload'),
            mergeMap(({ autocompleteField, ...request }: InitialValueDataRequest) =>
                paginationService
                    .getAutocompleteInitialValue({ autocompleteField, ...request })
                    .then((initialValueData) =>
                        actions.getInitialValueSuccess({
                            autocompleteField,
                            ...initialValueData,
                        }),
                    )
                    .catch(({ message }) =>
                        actions.getInitialValueFailed({
                            autocompleteField,
                            message,
                        }),
                    ),
            ),
        );

    return combineEpics(
        paginationEpic,
        paginationSuccessEpic,
        clearPaginationEpic,
        autocompleteEpic,
        getAutocompleteInitialValueEpic,
    );
};
